import * as React from 'react'

import { Plus } from 'lucide-react'
import { MobXProviderContext, observer } from 'mobx-react'
import { FormProvider, useWatch } from 'react-hook-form'

import { FormListSelector } from '~/client/src/shared/components/FormListSelector'
import FullScreenPickerSheet from '~/client/src/shared/components/FullScreenPickerSheet/FullScreenPickerSheet'
import { useResourceControlsTabContext } from '~/client/src/shared/components/ResourceControls/ResourceControlsTabContext'
import {
  SidePanelOverlayProvider,
  useSidePanelOverlayRoot,
} from '~/client/src/shared/components/SidePanel/SidePanelOverlayContext'
import {
  getResourceTypeLabels,
  getSelectFormTitle,
} from '~/client/src/shared/constants/resourceTypeLabels'
import { useTagsStore } from '~/client/src/shared/hooks/useTagsStore'
import {
  getResourceFieldDefinition,
  IResourceRuntimeContext,
} from '~/client/src/shared/resources/definitions'
import {
  isProjectLocationKind,
  LOCATION_KIND_PROPERTY,
} from '~/client/src/shared/resources/domain/locationKind'
import type { ResourceSchema } from '~/client/src/shared/resources/domain/resourceSchema'
import { renderResourceSemanticIcon } from '~/client/src/shared/resources/icons/resourceSemanticIcons'
import { createResourceDisplayValue } from '~/client/src/shared/resources/presentation/createResourceDisplayValue'
import {
  isEmptyResourceValue,
  RESOURCE_EMPTY_VALUE,
} from '~/client/src/shared/resources/presentation/formatResourceField'
import { resolveResourceValue } from '~/client/src/shared/resources/resolveResourceValue'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import Localization from '../../localization/LocalizationManager'
import { Button } from '../Button/Button'
import { DynamicForm, useDynamicForm } from '../DynamicForm'
import { DYNAMIC_FORM_ID, FieldType } from '../DynamicForm/types'
import { Loader } from '../Loader'
import { PanelSections } from '../PanelSections/PanelSections'
import { Badge } from '../shadcn-ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../shadcn-ui/dropdown-menu'
import { ScrollArea } from '../shadcn-ui/scroll-area'
import Spinner from '../Spinner/Spinner'
import StruxhubInputLabel from '../StruxhubInputs/components/StruxhubInputLabel'
import { ResourceActionsMenu } from './ResourceActionsMenu'
import {
  ResourceEditContext,
  ResourceEditContextValue,
  ResourceEditRecord,
  ResourceReadOnlyField,
  useResourceEdit,
  useResourceEditState,
} from './ResourceEditContext'
import {
  computeAddableFieldOptions,
  computeVisibleFields,
} from './resourceEditFieldVisibility'
import ResourceEditFormSelector from './ResourceEditFormSelector'
import { ResourceEditSections } from './ResourceEditSections'
import ResourceQRCodeHeader from './ResourceQRCodeHeader'
import { useDeriveFieldUpdates } from './useDeriveFieldUpdates'
import { useResourceFields } from './useResourceFields'
import { useResourceFileUpload } from './useResourceFileUpload'

interface IRootProps {
  recordId: string | null
  record?: ResourceEditRecord | null
  templateId?: string
  resourceTypeId: string
  runtimeContext: IResourceRuntimeContext
  qrCodeUrl?: string | null
  formId?: string
  onClose: () => void
  onSave: (formData: Record<string, any>) => Promise<void>
  onTemplateChange?: (templateId: string) => void
  /**
   * Root-field seed values (icon/color/parent) for creation mode only. Lets a
   * caller pre-fill those fields on a new resource — e.g. a map-drawn equipment
   * starts with the icon it was drawn with — without seeding name or custom data.
   */
  creationRootDefaults?: Record<string, any>
  /**
   * This form is rendered inside Maps. Creating elsewhere drops the resource's
   * Maps-only root fields (Parent, for equipment), since their value comes from
   * where the item is drawn. Editing shows them either way.
   */
  isMapsSurface?: boolean
  /**
   * Keep the card open after a successful create instead of closing it. The
   * maps card uses this so a freshly-created equipment stays open: its onSave
   * re-selects the saved map item, and the card re-renders in edit mode.
   */
  keepOpenOnCreate?: boolean
  /** Delete the resource. When provided, the header shows a 3-dot overflow menu
   * with a Delete action (hidden in creation mode). The menu does not confirm —
   * the caller owns confirmation. */
  onDelete?: () => void
  resourceSchema: ResourceSchema
  children: React.ReactNode
}

interface IMobXContext {
  projectDateStore?: ProjectDateStore
  // Spread from the root store; true on the mobile build.
  isMobileEnv?: boolean
}

const RootInner: React.FC<IRootProps> = ({
  recordId,
  record,
  templateId,
  resourceTypeId,
  runtimeContext,
  qrCodeUrl,
  formId: formIdProp,
  onClose,
  onSave,
  onTemplateChange,
  creationRootDefaults,
  isMapsSurface,
  keepOpenOnCreate,
  onDelete,
  resourceSchema,
  children,
}) => {
  const { projectDateStore } = React.useContext(
    MobXProviderContext,
  ) as IMobXContext
  const tagsStore = useTagsStore()
  // Present only when a controls-enabled body wraps the card in the provider.
  // The single footer Save commits this draft alongside the resource fields.
  const controlsTab = useResourceControlsTabContext()
  const resolvedFormId = formIdProp || DYNAMIC_FORM_ID
  const isCreationMode = recordId === 'new'
  const templates = resourceSchema.templates
  const propertyDefinitions = resourceSchema.properties

  const template = React.useMemo(() => {
    if (isCreationMode) {
      return templateId
        ? templates.find(t => t.id === templateId)
        : templates[0] || null
    }
    return (
      templates.find(t => t.id === record?.templateId) || templates[0] || null
    )
  }, [templates, templateId, record, isCreationMode])

  const getDisplayValue = React.useMemo(
    () =>
      createResourceDisplayValue({
        properties: propertyDefinitions.map(property => ({
          name: property.name,
          propertyType: property.propertyType,
          configuration: {
            options: property.configuration?.options?.map(option => ({
              value: option.value,
              label: option.label,
              parentValue: option.parentValue ?? undefined,
            })),
            unit: property.configuration?.unit || undefined,
            resourceLink: property.configuration?.resourceLink
              ? {
                  targetResourceType:
                    property.configuration.resourceLink.targetResourceType,
                }
              : undefined,
          },
        })),
        resourceTypeId,
        runtimeContext,
        projectDateStore,
        tagsStore,
      }),
    [
      propertyDefinitions,
      resourceTypeId,
      runtimeContext,
      projectDateStore,
      tagsStore,
    ],
  )

  // A project has exactly one site root and nothing may delete it - the backend's
  // softDeleteManyLocations takes whatever ids it is handed, so withholding the handler is
  // the only guard there is. Withheld rather than the menu hidden: Delete is the menu's only
  // item, so an empty menu would be the result either way, and disarming it here covers every
  // host - Root is the sole provider of this context.
  const { fields, updateField, isLocked } = useResourceFields({
    // In creation, seed only what creationRootDefaults carries — a map-drawn
    // equipment pre-fills the name and icon it was drawn with, plus color/parent —
    // leaving custom data empty for a fresh resource. Creation surfaces that pass
    // no defaults (the Objects tab) start every field empty.
    record: isCreationMode ? (creationRootDefaults ?? null) : record,
    template,
    propertyDefinitions,
    recordId,
    resourceTypeId,
    projectDateStore,
    fieldOptions: {
      optionalTemplateRequirements: !isCreationMode,
      // Edit shows the full property catalog so assigned non-template values
      // appear and the "Add field" control can offer the rest. Creation keeps
      // the template subset.
      includeAllProperties: !isCreationMode,
      isMapsSurface,
    },
  })

  // Nor can a viewer the card is locked for delete what they may not change.
  const deletableOnDelete =
    isLocked || isProjectLocationKind(record?.data?.[LOCATION_KIND_PROPERTY])
      ? undefined
      : onDelete

  // Fields the user revealed via "Add field" this session; survives the
  // useResourceFields state resets and is cleared when the record changes.
  const [revealedFieldNames, setRevealedFieldNames] = React.useState<
    Set<string>
  >(() => new Set())

  const notReadOnlyFields = React.useMemo(
    () => fields.filter(field => !field.readOnly),
    [fields],
  )

  // Edit mode shows only assigned (non-empty) fields plus required and
  // session-revealed ones; creation keeps the full template field set.
  const editableFields = React.useMemo(
    () =>
      isCreationMode
        ? notReadOnlyFields
        : computeVisibleFields(
            notReadOnlyFields,
            record?.data,
            revealedFieldNames,
          ),
    [notReadOnlyFields, isCreationMode, record, revealedFieldNames],
  )

  const addableFieldOptions = React.useMemo(
    () =>
      isCreationMode || isLocked
        ? []
        : computeAddableFieldOptions(
            notReadOnlyFields,
            new Set(editableFields.map(f => f.name)),
          ),
    [notReadOnlyFields, editableFields, isCreationMode, isLocked],
  )

  const revealField = React.useCallback((fieldName: string) => {
    setRevealedFieldNames(prev => {
      if (prev.has(fieldName)) return prev
      const next = new Set(prev)
      next.add(fieldName)
      return next
    })
  }, [])

  const readOnlyFields = React.useMemo<ResourceReadOnlyField[]>(() => {
    return fields
      .filter(field => field.readOnly)
      .map(field => {
        const fieldDefinition = getResourceFieldDefinition(
          resourceTypeId,
          field.name,
        )

        if (fieldDefinition?.card?.visible === false) {
          return null
        }

        const rawValue =
          (record as any)?.[field.name] ??
          resolveResourceValue({
            record: record || { data: {} },
            resourceTypeId,
            key: field.name,
          })

        if (isEmptyResourceValue(rawValue)) {
          return null
        }

        const text = getDisplayValue(field.name, rawValue)
        if (!text || text === RESOURCE_EMPTY_VALUE) {
          return null
        }

        const card = fieldDefinition?.card
        const statusBadge =
          card?.isStatus && card.resolveStatusBadge
            ? card.resolveStatusBadge(rawValue)
            : undefined

        return {
          key: field.name,
          label: field.label || field.name,
          text: statusBadge?.text || text,
          kind: statusBadge ? 'status' : card?.kind || 'text',
          icon: card?.iconKey
            ? renderResourceSemanticIcon(card.iconKey)
            : undefined,
          statusVariant: statusBadge?.variant,
        }
      })
      .filter(Boolean) as ResourceReadOnlyField[]
  }, [fields, getDisplayValue, record, resourceTypeId])

  const formMethods = useDynamicForm(editableFields)

  useDeriveFieldUpdates(formMethods, resourceTypeId, editableFields)

  const [isSaving, setIsSaving] = React.useState(false)
  const [saveError, setSaveError] = React.useState<string | null>(null)
  const [isFormSelectorOpen, setIsFormSelectorOpen] = React.useState(false)

  React.useEffect(() => {
    setRevealedFieldNames(new Set())
  }, [recordId])

  const openFormSelector = React.useCallback(() => {
    setIsFormSelectorOpen(true)
  }, [])

  const closeFormSelector = React.useCallback(() => {
    setIsFormSelectorOpen(false)
  }, [])

  const handleTemplateSelect = React.useCallback(
    (newTemplateId: string) => {
      onTemplateChange?.(newTemplateId)
      setIsFormSelectorOpen(false)
    },
    [onTemplateChange],
  )

  const loading = resourceSchema.loading

  const handleSave = React.useCallback(
    async (formData: Record<string, unknown>) => {
      // Nothing on a locked card can change, so there is nothing to write.
      if (isLocked) {
        onClose()
        return
      }
      setIsSaving(true)
      setSaveError(null)

      const resolvedTemplateId = isCreationMode
        ? template?.id
        : record?.templateId
      const allowedFieldNames = new Set(editableFields.map(f => f.name))
      const numberFieldNames = new Set(
        editableFields
          .filter(field => field.type === FieldType.NUMBER)
          .map(f => f.name),
      )
      const dateTimeFieldNames = new Set(
        editableFields
          .filter(field => field.type === FieldType.DATETIME)
          .map(f => f.name),
      )
      const normalizedFormData = Object.entries(formData).reduce(
        (acc, [key, value]) => {
          if (!allowedFieldNames.has(key)) return acc
          if (
            numberFieldNames.has(key) &&
            value !== null &&
            value !== undefined &&
            value !== ''
          ) {
            const numericValue = Number(value)
            acc[key] = Number.isFinite(numericValue) ? numericValue : value
            return acc
          }
          if (dateTimeFieldNames.has(key)) {
            acc[key] =
              value === '' || value === 0 || value === '0' ? null : value
            return acc
          }
          acc[key] = value
          return acc
        },
        {} as Record<string, unknown>,
      )

      try {
        // Commit the Controls draft first (if the card has a Controls section).
        // It surfaces the cascade-confirmation dialog and returns false if the
        // admin cancels it — abort the whole Save then, leaving the panel open.
        if (controlsTab && !controlsTab.readOnly) {
          const controlsCommitted = await controlsTab.save()
          if (!controlsCommitted) return
        }
        await onSave({
          ...normalizedFormData,
          ...controlsTab?.getResourceSaveOverrides(),
          _templateId: resolvedTemplateId,
        })
        // Both halves persisted — only now clear the controls dirty flags. If
        // onSave threw above, this is skipped so the draft stays dirty and a
        // retry re-commits controls + fields together (rather than leaving the
        // already-persisted controls silently out of sync with unsaved fields).
        if (controlsTab && !controlsTab.readOnly) controlsTab.markSaved()
        if (!(isCreationMode && keepOpenOnCreate)) onClose()
      } catch (error) {
        console.error('Failed to save:', error)
        setSaveError(Localization.translator.failedToSaveChangesTryAgain)
      } finally {
        setIsSaving(false)
      }
    },
    [
      isLocked,
      editableFields,
      isCreationMode,
      template,
      record,
      onSave,
      onClose,
      controlsTab,
      keepOpenOnCreate,
    ],
  )

  const contextValue = React.useMemo(
    (): ResourceEditContextValue => ({
      state: {
        recordId,
        record: record || null,
        isCreationMode,
        isLocked,
        isSaving,
        saveError,
        editableFields,
        addableFieldOptions,
        readOnlyFields,
        loading,
        templateName: template?.name || null,
        resourceTypeId,
        qrCodeUrl: qrCodeUrl || null,
        formId: resolvedFormId,
        isFormSelectorOpen,
      },
      actions: {
        close: onClose,
        save: handleSave,
        updateField,
        revealField,
        openFormSelector,
        closeFormSelector,
        onDelete: deletableOnDelete,
      },
    }),
    [
      recordId,
      record,
      isCreationMode,
      isLocked,
      isSaving,
      saveError,
      editableFields,
      addableFieldOptions,
      readOnlyFields,
      loading,
      template,
      resourceTypeId,
      qrCodeUrl,
      resolvedFormId,
      isFormSelectorOpen,
      onClose,
      handleSave,
      updateField,
      revealField,
      openFormSelector,
      closeFormSelector,
      onDelete,
    ],
  )

  if (loading) {
    return <Loader />
  }

  return (
    <ResourceEditContext.Provider value={contextValue}>
      <FormProvider {...formMethods}>
        {/* Self-provide the picker-sheet overlay root so field/form sheets cover
            the whole card — including the footer — even in hosts without a
            SidePanel (the Maps view). */}
        <div className="tw:relative tw:flex tw:flex-col tw:h-full">
          <SidePanelOverlayProvider>
            {children}
            <FullScreenPickerSheet
              isOpen={isFormSelectorOpen}
              onClose={closeFormSelector}
            >
              <FormListSelector
                title={getSelectFormTitle(resourceTypeId)}
                resourceTypeId={resourceTypeId}
                onSelect={handleTemplateSelect}
                onClose={closeFormSelector}
                templates={templates}
                loading={loading}
              />
            </FullScreenPickerSheet>
          </SidePanelOverlayProvider>
        </div>
      </FormProvider>
    </ResourceEditContext.Provider>
  )
}

const Root = observer(RootInner)

interface IHeaderProps {
  trailing?: React.ReactNode
}

const Header: React.FC<IHeaderProps> = ({ trailing }) => {
  const {
    state: { record, isCreationMode, templateName, resourceTypeId },
    actions: { close, onDelete },
  } = useResourceEdit()

  const title = React.useMemo(() => {
    if (isCreationMode) {
      return getResourceTypeLabels(resourceTypeId).createTitle()
    }
    const stxId = record?.stxResourceId
    return templateName && stxId
      ? `${templateName}: ${stxId}`
      : templateName && record?.name
        ? `${templateName}: ${record.name}`
        : record?.name || ''
  }, [isCreationMode, record, templateName, resourceTypeId])

  return (
    <PanelSections.Header
      title={title}
      onClose={close}
      closeTestId="resource-edit-close"
      actions={
        <>
          {trailing}
          {onDelete && !isCreationMode && (
            <ResourceActionsMenu onDelete={onDelete} />
          )}
        </>
      }
    />
  )
}

const QRCode: React.FC = () => {
  const {
    state: { isCreationMode, qrCodeUrl, record },
  } = useResourceEdit()

  if (isCreationMode || !qrCodeUrl || !record) return null

  return <ResourceQRCodeHeader name={record.name} url={qrCodeUrl} />
}

interface IContentProps {
  children: React.ReactNode
}

const Content: React.FC<IContentProps> = ({ children }) => {
  return (
    <PanelSections.Content className="tw:overflow-hidden">
      <ScrollArea className="tw:size-full">
        {/* Isolate so the sticky tab bar's z-index stays contained within the
            viewport and doesn't paint over the scrollbar (a sibling overlay). */}
        <div className="tw:isolate">{children}</div>
      </ScrollArea>
    </PanelSections.Content>
  )
}

// ResourceEdit.Sections owns the Properties tab + secondary-nav chrome; the
// Form is just the general section's body.
const Form: React.FC = () => (
  <>
    <ReadOnlyFieldsSection />
    <EditableFieldsSection />
  </>
)

const EditableFieldsSection: React.FC = () => {
  const fileUploadFn = useResourceFileUpload()
  const { isMobileEnv } = React.useContext(MobXProviderContext) as IMobXContext
  const overlayRoot = useSidePanelOverlayRoot()
  // On mobile the card lives in a vaul Drawer, whose Radix overlay installs a
  // react-remove-scroll lock — a non-passive document `touchmove` handler that
  // preventDefaults any gesture landing outside the drawer. base-ui portals menus
  // to <body>, so this list renders fine but cannot be finger-scrolled at all.
  // Portal it into the overlay root ResourceEdit.Root self-provides instead: in
  // the sheet that node sits inside the DrawerContent, which is what lets the
  // touch through. Desktop holds no lock and keeps the default body portal.
  const menuContainer = isMobileEnv ? (overlayRoot ?? undefined) : undefined
  const {
    state: {
      editableFields,
      addableFieldOptions,
      formId,
      recordId,
      isCreationMode,
    },
    actions: { save, updateField, revealField },
  } = useResourceEdit()

  return (
    <>
      {editableFields.length > 0 && (
        <DynamicForm
          key={recordId}
          fields={editableFields}
          onSubmit={save}
          fileUploadFn={fileUploadFn}
          className="tw:flex tw:flex-col tw:gap-4"
          hideSubmitButton={true}
          hideHelperText={true}
          formId={formId}
          onFieldsChange={updateField}
          // Fields can live in a hidden (display:none) tab; skip native
          // validation so Save uses RHF rules instead of choking on hidden inputs.
          noValidate={true}
        />
      )}
      {!isCreationMode && addableFieldOptions.length > 0 && (
        <div className="tw:flex tw:justify-end tw:pt-3">
          <DropdownMenu>
            <DropdownMenuTrigger className="tw:inline-flex tw:items-center tw:gap-1 tw:rounded-md tw:border-0 tw:bg-transparent tw:px-2 tw:py-1 tw:text-body-sm tw:text-primary tw:cursor-pointer tw:outline-none tw:hover:bg-muted tw:focus-visible:ring-2 tw:focus-visible:ring-ring">
              <Plus className="tw:size-4" />
              {Localization.translator.addProperty}
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              container={menuContainer}
              // Inside the sheet, a pull-down while the list is at scrollTop 0
              // would drag the drawer instead of scrolling the list.
              data-vaul-no-drag=""
              // min(): a bare cap here would replace the primitive's
              // --available-height clamp, leaving part of the list off-screen
              // and unreachable whenever the viewport is short (keyboard up).
              className="tw:w-64 tw:max-h-[min(--spacing(72),var(--available-height,85vh))] tw:overscroll-contain"
            >
              {addableFieldOptions.map(option => (
                <DropdownMenuItem
                  key={String(option.value)}
                  onClick={() => revealField(String(option.value))}
                >
                  {option.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </>
  )
}

const ReadOnlyFieldsSection: React.FC = () => {
  const {
    state: { readOnlyFields },
  } = useResourceEdit()

  if (readOnlyFields.length === 0) {
    return null
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-4 tw:py-4">
      {readOnlyFields.map(field => (
        <ReadOnlyFieldRow key={field.key} field={field} />
      ))}
    </div>
  )
}

const ReadOnlyFieldRow: React.FC<{ field: ResourceReadOnlyField }> = ({
  field,
}) => {
  return (
    <div className="tw:flex tw:flex-col">
      <StruxhubInputLabel
        label={field.label}
        value={field.text}
        isRequired={false}
        isValid={true}
        isRequiredTextHidden={true}
      />
      {field.kind === 'status' ? (
        <div className="tw:relative tw:px-2">
          <Badge variant={field.statusVariant || 'muted'}>{field.text}</Badge>
        </div>
      ) : (
        <div className="tw:relative tw:flex tw:items-center tw:gap-1 tw:min-h-10 tw:px-2 tw:text-body-lg tw:text-foreground">
          {field.icon && (
            <span className="tw:size-6 tw:shrink-0 tw:inline-flex tw:items-center tw:justify-center tw:[&_svg]:size-6">
              {field.icon}
            </span>
          )}
          <span
            className={
              field.kind === 'link' ? 'tw:underline tw:underline-offset-2' : ''
            }
          >
            {field.text}
          </span>
        </div>
      )}
    </div>
  )
}

const Footer: React.FC = () => {
  const {
    state: {
      isSaving,
      isCreationMode,
      isLocked,
      saveError,
      formId,
      editableFields,
    },
    actions: { close },
  } = useResourceEdit()

  const watchedValues = useWatch<Record<string, unknown>>()
  const hasMissingRequired = editableFields.some(field => {
    if (!field.required) return false
    const value = watchedValues[field.name]
    if (Array.isArray(value)) return value.length === 0
    if (typeof value === 'string') return value.trim() === ''
    return value === null || value === undefined
  })

  // A locked card has nothing to save; the header's close button closes it.
  if (isLocked) return null

  // Single Cancel/Save for the whole card. The submit persists the resource
  // fields and — via handleSave — the Controls section's draft in one action, so
  // sections don't carry their own Save.
  return (
    <PanelSections.Footer>
      {saveError && (
        <div className="tw:text-destructive tw:mb-2">{saveError}</div>
      )}
      <div className="tw:flex tw:gap-2">
        <Button
          type="button"
          variant="outline"
          size="lg"
          className="tw:flex-1"
          onClick={close}
          disabled={isSaving}
        >
          {Localization.translator.cancel}
        </Button>
        <Button
          type="submit"
          variant="default"
          size="lg"
          className={`tw:flex-1 tw:disabled:bg-muted tw:disabled:text-muted-foreground${
            hasMissingRequired ? ' tw:bg-primary/60' : ''
          }`}
          form={formId}
          disabled={isSaving}
          data-testid="resource-edit-save"
        >
          {isSaving && (
            <span data-icon="inline-start" className="tw:inline-flex">
              <Spinner size="sm" variant="white" />
            </span>
          )}
          {isCreationMode
            ? Localization.translator.create
            : Localization.translator.save}
        </Button>
      </div>
    </PanelSections.Footer>
  )
}

export const ResourceEdit = {
  Root,
  Header,
  QRCode,
  Content,
  Sections: ResourceEditSections,
  Form,
  FormSelector: ResourceEditFormSelector,
  Footer,
  useResourceEdit,
  useResourceEditState,
}
